import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { handleUnauthorizedResponse } from '../../api/http';
import ReportsApi from '../../api/ReportsApi';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import SelectField from '../fields/SelectField';
import { errorToast } from '../Toast';
import AllPoliciesReport from './AllPoliciesReport';
import ClaimReservesReport from './ClaimReservesReport';
import ClaimsPayableReport from './ClaimsPayableReport';
import ClaimsReport from './ClaimsReport';
import FinancialTransactionsReport from './FinancialTransactionsReport';
import OnRiskReport from './OnRiskReport';
import PaidFinancialTransactionsReport from './PaidFinancialTransactionsReport';
import PayableCommissionsReport from './PayableCommissionsReport';
import UnearnedPremiumReport from './UnearnedPremiumReport';
import { checkCanRunReport, getPolicyReportReq } from './util';

interface Props {
	authData: AuthData;
	configData: ConfigData;
}

const ReportsView = (props: Props) => {
	const { configData, authData } = props;
	const { productConfigs } = configData;
	const [reportType, setReportType] = useState('');
	const [canRunReport, setCanRunReport] = useState(false);
	const [reportDetails, setReportDetails] = useState({});
	const [report, setReport] = useState(null);
	const [csvLink, setCSVLink] = useState(null);
	const [editMode, setEditMode] = useState(true);
	const [loadingResults, setLoadingResults] = useState(false);

	const reportTypes = [
		'All Policies Report',
		'Claims Payable Report',
		'Claims Report',
		'Claims Reserves Report',
		'Financial Transactions Report',
		'On-Risk Report',
		'Paid Financial Transactions Report',
		'Payable Commissions Report',
		'Unearned Premium (A/R) Report',
	];

	const goBack = () => {
		setReport(null);
		setCSVLink(null);
		setEditMode(true);
	};

	const updateReportType = (name: string, type: string, val: string) => {
		setReportType(val);
		setCanRunReport(false);
		setReport(null);
		setCSVLink(null);
		setReportDetails({});
	};

	const runReport = () => {
		switch (reportType) {
			case 'All Policies Report':
				runAllPoliciesReport();
				break;
			case 'Claims Payable Report':
				runClaimsPayablesReport();
				break;
			case 'Claims Report':
				runClaimsReport();
				break;
			case 'Claims Reserves Report':
				runClaimReservesReport();
				break;
			case 'Financial Transactions Report':
				runFinancialTransactionsReport();
				break;
			case 'On-Risk Report':
				runOnRiskReport();
				break;
			case 'Paid Financial Transactions Report':
				runPaidFinancialTransactionsReport();
				break;
			case 'Payable Commissions Report':
				runPayableCommissionsReport();
				break;
			case 'Unearned Premium (A/R) Report':
				runUnearnedPremiumReport();
				break;
		}
	};

	const getReportStatus = async (locator: string) => {
		try {
			const status = await ReportsApi.getReportStatus(
				authData.auth.authorizationToken,
				locator,
			);
			if (status.data.status !== 'complete') {
				// if status of report is not complete, wait 1 second then try again
				setTimeout(() => getReportStatus(locator), 1000);
			} else {
				setEditMode(false);
				setCSVLink(status.data.resultUrl);
				setLoadingResults(false);
			}
		} catch (error) {
			errorToast('Failed to get report status');
			setLoadingResults(false);
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
	};

	const runAllPoliciesReport = async () => {
		const req = getPolicyReportReq(reportDetails, productConfigs);
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runAllPoliciesReport(
				authData.auth.authorizationToken,
				req,
			);
			const preview = await ReportsApi.runAllPoliciesReportPreview(
				authData.auth.authorizationToken,
				req,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runOnRiskReport = async () => {
		const req = getPolicyReportReq(reportDetails, productConfigs);
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runOnRiskReport(
				authData.auth.authorizationToken,
				req,
			);
			const preview = await ReportsApi.runOnRiskReportPreview(
				authData.auth.authorizationToken,
				req,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runClaimsPayablesReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runClaimsPayableReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runClaimsPayableReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runClaimsReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runClaimsReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runClaimsReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runClaimReservesReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runClaimsReservesReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runClaimsReservesReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runFinancialTransactionsReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runFinancialTransactionsReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runFinancialTransactionsReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runPaidFinancialTransactionsReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runPaidFinancialTransactionsReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview =
				await ReportsApi.runPaidFinancialTransactionsReportPreview(
					authData.auth.authorizationToken,
					reportDetails,
				);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runPayableCommissionsReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runPayableCommissionsReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runPayableCommissionsReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const runUnearnedPremiumReport = async () => {
		try {
			setLoadingResults(true);
			const locator = await ReportsApi.runUnearnedPremiumReport(
				authData.auth.authorizationToken,
				reportDetails,
			);
			const preview = await ReportsApi.runUnearnedPremiumReportPreview(
				authData.auth.authorizationToken,
				reportDetails,
			);
			getReportStatus(locator.data.locator);
			setReport(preview.data);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to run report');
			setLoadingResults(false);
		}
	};

	const updateReportDetails = (req: any, errors: boolean) => {
		const reqIsValid = !errors && checkCanRunReport(reportType, req);
		setCanRunReport(reqIsValid);
		setReportDetails(req);
	};

	const renderReportDetails = (): any => {
		switch (reportType) {
			case 'All Policies Report':
				return (
					<AllPoliciesReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Claims Payable Report':
				return (
					<ClaimsPayableReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Claims Report':
				return (
					<ClaimsReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Claims Reserves Report':
				return (
					<ClaimReservesReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Financial Transactions Report':
				return (
					<FinancialTransactionsReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'On-Risk Report':
				return (
					<OnRiskReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Paid Financial Transactions Report':
				return (
					<PaidFinancialTransactionsReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Payable Commissions Report':
				return (
					<PayableCommissionsReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
			case 'Unearned Premium (A/R) Report':
				return (
					<UnearnedPremiumReport
						reportResults={report}
						loadingResults={loadingResults}
						onUpdate={updateReportDetails}
					/>
				);
		}
	};

	const getButtons = () => {
		const backgroundColor =
			canRunReport && !loadingResults ? '#02457A' : 'rgb(2, 69, 122, 0.44)';
		const cursor = canRunReport ? 'pointer' : 'not-allowed';
		if (!editMode) {
			return (
				<>
					<Button
						color='primary'
						style={{ backgroundColor, color: '#FFFFFF', margin: '5px', cursor }}
						onClick={goBack}
					>
						Back
					</Button>
					{csvLink && (
						<Button
							color='primary'
							style={{
								backgroundColor,
								color: '#FFFFFF',
								margin: '5px',
								cursor,
							}}
							href={csvLink}
						>
							{' '}
							Download CSV
						</Button>
					)}
				</>
			);
		}
		return (
			<Button
				color='primary'
				style={{ backgroundColor, color: '#FFFFFF', margin: '5px', cursor }}
				disabled={loadingResults}
				onClick={canRunReport ? runReport : null}
			>
				{' '}
				Run Report
			</Button>
		);
	};

	const reportOptions = reportTypes.map((r) => {
		return { value: r, label: r };
	});

	return (
		<Stack spacing={2}>
			<Stack direction='row' justifyContent='flex-end' spacing={2}>
				{getButtons()}
			</Stack>
			<SelectField
				title='Choose Report Type'
				options={reportOptions}
				value={reportType}
				disabled={!editMode || loadingResults}
				required
				onUpdate={updateReportType}
			/>
			{renderReportDetails()}
		</Stack>
	);
};

function withContext(Component: any) {
	return (
		props: Omit<Props, 'authData' | 'policyholderData' | 'configData'>,
	) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(ReportsView);
